import { Lang, Project, Reward as RawReward } from '@owl-nest/api-client/latest'
import * as date from '@owl-nest/date'
import { t } from '@owl-nest/localize'
import * as env from '@owl-nest/config'

import * as i18n from './i18n'
import * as imageModel from './image'
import * as optionModel from './option'
import * as variantModel from './variant'
import * as projectModel from './project/base'

export type Reward<PROJECT_EXTRA_FIELDS extends string = ''> = Omit<RawReward, 'variants' | 'project'> & {
  project: Project<PROJECT_EXTRA_FIELDS>
  options: optionModel.Option[]
  variants: variantModel.Variant<PROJECT_EXTRA_FIELDS>[]
}

export type RewardStock = {
  available?: number | null
  taken: number
  total?: number | null
}

export function Reward<PROJECT_EXTRA_FIELDS extends string = ''>(
  rawReward: RawReward,
  project: Project<PROJECT_EXTRA_FIELDS>,
): Reward<PROJECT_EXTRA_FIELDS> {
  const { variants: _, ...reward } = rawReward

  const isMembership = projectModel.isMembership(project)

  const rewardWithParent = {
    ...reward,
    project,
  } as Reward<PROJECT_EXTRA_FIELDS>

  if (isMembership && rawReward.is_free) {
    rewardWithParent.title = { [project.lang]: (reward.title && reward.title[project.lang]) || t('Free membership') }
    rewardWithParent.description = {
      [project.lang]:
        (reward.description && reward.description[project.lang]) ||
        t('Join the community and be notified of upcoming public posts.'),
    }
  }

  const options = rawReward.options?.map((option) => optionModel.Option(option, rewardWithParent)) || []
  const variants = rawReward.variants?.map((variant) => variantModel.Variant(variant, rewardWithParent)) || []

  return {
    ...rewardWithParent,
    options,
    variants,
  }
}

/**
 * Returns the reward description in the user's locale. If none, falls back to the project language.
 * @param reward
 * @param lang
 */
export function description<PROJECT_EXTRA_FIELDS extends string = ''>(
  reward: Reward<PROJECT_EXTRA_FIELDS>,
  lang: Lang = env.getUserConfig().lang,
): string {
  // TODO: add project lang as fallback language
  return i18n.getWithLang(reward.description, lang) || ''
}

/**
 * Formats the reward delivery date in the user's locale.
 * Defaults to the 'MONTH year' format.
 * @param reward
 * @param format Optional - Unicode token to describe the desired format
 */
export function formattedDeliveryDate<PROJECT_EXTRA_FIELDS extends string = ''>(
  reward: Reward<PROJECT_EXTRA_FIELDS>,
  format = 'MMMM yyyy',
): string {
  const rewardDeliveryDate = reward.shippings?.date_delivery
  return rewardDeliveryDate ? date.format(date.parseISO(rewardDeliveryDate), format) : ''
}

/**
 * Checks if a reward has any options or variants available. We check variants to account for older projects
 *  that have no "options" attribute.
 * @param reward - The reward object to check
 * @returns True if the reward has at least one option or variant, false otherwise
 */
export function hasChildren<PROJECT_EXTRA_FIELDS extends string = ''>(reward: Reward<PROJECT_EXTRA_FIELDS>) {
  return (reward.options && reward.options.length > 0) || (reward.variants && reward.variants.length > 0)
}

/**
 * Returns the reward image for the user's locale. If none, falls back to the project language.
 * @param reward
 * @param version
 * @param lang
 */
export function image<PROJECT_EXTRA_FIELDS extends string = ''>(
  reward: Reward<PROJECT_EXTRA_FIELDS>,
  version = 'full',
  lang: Lang = env.getUserConfig().lang,
): string | undefined {
  if (reward.image === undefined) return undefined

  // TODO: add project lang as fallback language
  const imageResource = i18n.getWithLang(reward.image, lang)
  return imageModel.getUrl(imageResource?.versions, version)
}

/**
 * Determines whether the reward is a legacy reward (doesn't have a title)
 * @param reward
 */
export function isLegacy<PROJECT_EXTRA_FIELDS extends string = ''>(reward: Reward<PROJECT_EXTRA_FIELDS>): boolean {
  return reward.title === undefined
}

/**
 * Groups the reward stock data in a handy format.
 * @param reward
 */
export function stock<PROJECT_EXTRA_FIELDS extends string = ''>(reward: Reward<PROJECT_EXTRA_FIELDS>): RewardStock {
  return {
    available: reward.aware_stock_available,
    taken: reward.stock_taken,
    total: reward.aware_stock,
  }
}

/**
 * Returns the reward title in the user's locale. Otherwise, falls back to the project language.
 * @param reward
 * @param lang
 */
export function title<PROJECT_EXTRA_FIELDS extends string = ''>(
  reward: Reward<PROJECT_EXTRA_FIELDS>,
  lang: Lang = env.getUserConfig().lang,
): string | undefined {
  if (isLegacy(reward)) return undefined

  // TODO: add project lang as fallback language
  return i18n.getWithLang(reward.title, lang)
}

export function languages<PROJECT_EXTRA_FIELDS extends string = ''>(reward: Reward<PROJECT_EXTRA_FIELDS>) {
  if (isLegacy(reward)) {
    return Object.keys(reward.description)
  }
  return Object.keys(reward.title)
}

export function isTaken<PROJECT_EXTRA_FIELDS extends string = ''>(reward: Reward<PROJECT_EXTRA_FIELDS>) {
  if (reward.variants.length === 0) {
    return reward.stock_taken !== 0
  }
  return reward.variants.some((variant) => variant.stock_taken !== 0)
}

export function getShippingFormattedDate(reward: Reward, format = 'MMMM yyyy') {
  if (reward.shippings?.date_delivery === undefined) {
    return ''
  }

  return date.format(
    date.parseISO(date.createDateWithTimezone(new Date(reward.shippings.date_delivery), 'Etc/UTC').toISOString()),
    format,
  )
}

export function getVariantsCount(reward: Reward): number {
  // No variant
  if (reward.variants_count === 0) {
    return 0
  }

  // Reward is combined
  if (reward.is_combined) {
    return reward.variants_count
  }

  //Reward is not combined, the number of variants is the sum of values in each option
  return reward.options.reduce((nbVariants, currentOption) => nbVariants + currentOption.values.length, 0)
}

export function getVisibility(reward: Reward): 'archived' | 'normal' | 'hidden' | 'featured' {
  if (reward.is_featured) {
    return 'featured'
  } else if (reward.is_hidden) {
    return 'hidden'
  } else if (reward.is_archived) {
    return 'archived'
  } else {
    return 'normal'
  }
}
